import React from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import CategoryFilter from './filterFormatters/CategoryFilter';
import CategoryRenderer from './filterRenderers/CategoryRenderer';
import ResourceFilter from './filterFormatters/ResourceFilter';
import ResourceRenderer from './filterRenderers/ResourceRenderer';
import { DataGrid } from '../DataGrid';
import camelize from '../../../afosto-utils/src/string/camelize';
import capitalize from '@afosto/utils/string/capitalize';
import createDefaultView from '../DataSource/modules/views/utils';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import { logging as loggingApi } from '@afosto/iam/api/Logging';
import { actorFormatter, codeFormatter } from './formatters';

const fetchLogs = async dataSource =>
  dataSourceRequest(
    dataSource,
    async ({ filters }, requestOptions) => {
      const { activeFilters = [], fixedFilters = [] } = filters ?? {};
      const formattedFilters = [...fixedFilters, ...activeFilters].map(
        ({ key, operator, value }) => ({
          key,
          operator,
          values: value ?? [],
        }),
      );

      return loggingApi.queryLogs(
        {
          filter: formattedFilters,
        },
        requestOptions,
      );
    },
    {
      transformData: data =>
        data.reduce((acc, log) => {
          const code = camelize(log.code.replace('.', '_').replace(':', '_'));
          const resourceType = log?.resource?.entity_type
            ? camelize(log?.resource?.entity_type)
            : undefined;

          return [
            ...acc,
            {
              ...log,
              category: log?.category?.toLowerCase(),
              formattedCode: code,
              url: log?.metadata?.url,
              stream: log?.resource?.stream,
              trace: log?.resource?.trace,
              resourceType,
            },
          ];
        }, []),
    },
  );

const LOGS_PROFILE_MODEL = 'logs';
const LOGS_PROFILE_PATH = 'iam.logs';

const LogsGrid = ({ fixedFilters }) => {
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:logs:read']);

  const defaultProperties = [
    {
      key: 'actor',
      name: intl.formatMessage({
        id: 'dataGrid.columns.auditLogs.actor',
        defaultMessage: 'Actor',
        description: 'The column name for column actor',
      }),
      type: 'actor',
    },
    {
      key: 'category',
      name: intl.formatMessage({
        id: 'dataGrid.columns.auditLogs.category',
        defaultMessage: 'Category',
        description: 'The column name for column category',
      }),
      type: 'translation',
      formatOptions: {
        translationKey: 'auditCategories.{value}',
      },
    },
    {
      key: 'resourceType',
      name: intl.formatMessage({
        id: 'dataGrid.columns.auditLogs.resource',
        defaultMessage: 'Resource',
        description: 'The column name for column resource type',
      }),
      type: 'translation',
      formatOptions: {
        translationKey: 'auditEntities.{value}',
      },
    },
    {
      key: 'code',
      name: intl.formatMessage({
        id: 'dataGrid.columns.auditLogs.action',
        defaultMessage: 'Action / Code',
        description: 'The column name for column code',
      }),
      type: 'code',
    },
    {
      key: 'processed_at',
      name: intl.formatMessage({
        id: 'dataGrid.columns.auditLogs.processedAt',
        defaultMessage: 'Processed at',
        description: 'The column name for column processed_at',
      }),
      type: 'datetime',
    },
    {
      key: 'created_at',
      name: intl.formatMessage({
        id: 'dataGrid.columns.auditLogs.createdAt',
        defaultMessage: 'Created at',
        description: 'The column name for column created_at',
      }),
      type: 'datetime',
    },
  ];

  const { isFetchingProfile, views } = useDataSourceProfile({
    model: LOGS_PROFILE_MODEL,
    profilePath: LOGS_PROFILE_PATH,
    shouldFetchDefinitions: false,
  });
  const dataSource = useDataGrid(fetchLogs, {
    modules: ['dataProperties', 'filters', 'pagination', 'views'],
    gridOptions: {
      columnSelection: true,
      filterSelection: true,
      viewsSelection: true,
      copyGridUrl: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isFetchingProfile,
      storeStateKey: 'audit_logs',
    },
    dataProperties: {
      properties: [
        ...defaultProperties,
        {
          key: 'url',
          name: intl.formatMessage({
            id: 'dataGrid.columns.auditLogs.url',
            defaultMessage: 'Url',
            description: 'The column name for column url',
          }),
        },
        {
          key: 'stream',
          name: intl.formatMessage({
            id: 'dataGrid.columns.auditLogs.stream',
            defaultMessage: 'Stream',
            description: 'The column name for column stream',
          }),
        },
        {
          key: 'trace',
          name: intl.formatMessage({
            id: 'dataGrid.columns.auditLogs.trace',
            defaultMessage: 'Trace',
            description: 'The column name for column trace',
          }),
        },
      ],
    },
    filters: {
      filters: [
        {
          key: 'category',
          name: intl.formatMessage({
            id: `dataGrid.columns.auditLogs.category`,
            defaultMessage: 'Category',
            description: `The filter name for column category`,
          }),
          type: 'category',
          operators: ['in'],
          filterOptions: [
            {
              label: intl.formatMessage({
                id: 'auditCategories.audit',
                defaultMessage: 'Audit',
                description: 'Label of the filter option audit',
              }),
              value: 'AUDIT',
            },
            {
              label: intl.formatMessage({
                id: 'auditCategories.error',
                defaultMessage: 'Error',
                description: 'Label of the filter option error',
              }),
              value: 'ERROR',
            },
            {
              label: intl.formatMessage({
                id: 'auditCategories.request',
                defaultMessage: 'Request',
                description: 'Label of the filter option request',
              }),
              value: 'REQUEST',
            },
          ],
        },
        {
          key: 'resource.entity_type',
          name: intl.formatMessage({
            id: `dataGrid.columns.auditLogs.resource`,
            defaultMessage: 'Resource',
            description: `The filter name for column resource`,
          }),
          type: 'resource',
          operators: ['in'],
          filterOptions: [
            'user',
            'contact',
            'organisation',
            'dataflow',
            'order',
            'invoice',
            'calculation',
            'transaction',
            'shipment',
            'list',
            'parcel',
          ].map(type => ({
            label: intl.formatMessage({
              id: `auditEntities.${type}`,
              defaultMessage: capitalize(type),
              description: `Label of the filter option audit resource ${type}`,
            }),
            value: type,
          })),
        },
      ],
      fixedFilters,
    },
    views: {
      canCreateViewForTenant: sessionManager && sessionManager.hasPermissions(['iam:logs:write']),
      defaultView: createDefaultView(intl, {
        properties: defaultProperties,
        filters: [
          {
            key: 'category',
            operator: 'in',
            value: ['AUDIT', 'ERROR'],
          },
        ],
        sort: [],
      }),
      views,
      viewShortcuts: true,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(LOGS_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(LOGS_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(LOGS_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(LOGS_PROFILE_PATH)(repositionedViews),
    },
  });

  if (!hasPermissions()) {
    return <UnauthorizedComponent />;
  }

  return (
    <DataGrid
      dataSource={dataSource}
      formatters={{ actor: actorFormatter, code: codeFormatter }}
      filterFormatters={{
        category: CategoryFilter,
        resource: ResourceFilter,
      }}
      filterRenderers={{
        category: CategoryRenderer,
        resource: ResourceRenderer,
      }}
    />
  );
};

LogsGrid.propTypes = {
  fixedFilters: PropTypes.array,
};

LogsGrid.defaultProps = {
  fixedFilters: [],
};

export default LogsGrid;
